import { Service } from 'typedi';
import { BaseService, IBaseService, ServerResponse } from '../../../core/services/BaseService';
import { Appl } from '../../../core/Appl';
import { RetirementCalcUrlModel } from './calculator/RetirementCalcModels';

export interface IRetirementCalcService extends IBaseService {
  // Authenticated deep-link entry: resolves the portal-issued URL/referrer to
  // a memberId plus MBASE-sourced autofill data. Mirrors SPC's
  // getMemberInfoByUrlAsync (see ../spc/SpcService.ts). No backend endpoint
  // exists for this yet — this is the contract the frontend expects once one
  // is built.
  getMemberInfoByUrlAsync(model: RetirementCalcUrlModel): Promise<ServerResponse>;
  // All calculator reference data (tier config, ERF, mortality, Option 1,
  // assumption periods, nonspouse survivor limits, help text, beneficiary
  // relationships). Public endpoint; the caller falls back to the bundled
  // static tables if it fails or times out.
  getReferenceDataAsync(): Promise<ServerResponse>;
}

@Service() // eslint-disable-next-line
export class RetirementCalcService extends BaseService implements IRetirementCalcService {
  // The request shows the app-wide spinner, which blocks the page, and
  // Calculate waits for it — so a hung backend must give up and let the
  // bundled tables take over. Normally answers in about a second.
  private static readonly ReferenceDataTimeoutMs = 10000;

  constructor() {
    super(`${Appl.Setting.Services.RetirementServiceUrl}`);
  }

  public async getMemberInfoByUrlAsync(model: RetirementCalcUrlModel): Promise<ServerResponse> {
    try {
      const response = await this.client.post('/retirement-calculator/member-info-by-url', model);
      return this.getResponse(response);
    } catch (error: unknown) {
      return await this.getErrorResponse(error);
    }
  }

  public async getReferenceDataAsync(): Promise<ServerResponse> {
    try {
      const response = await this.client.get('/retirement-calculator/reference-data', { timeout: RetirementCalcService.ReferenceDataTimeoutMs });
      return this.getResponse(response);
    } catch (error: unknown) {
      return await this.getErrorResponse(error);
    }
  }
}
